import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Plus } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia, type Tono } from "@/componentes/ui/Insignia"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha, hoyLocal } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

import { documentoDeIndicadores } from "./exportarIndicadores"

type Indicador = {
  id: number
  nombre: string
  descripcion: string | null
  departamento_id: number
  departamento: string
  unidad: string
  sentido: "MAYOR" | "MENOR"
  linea_base: string | null
  meta: string
  tolerancia: number
  frecuencia: "MENSUAL" | "TRIMESTRAL"
  fuente: string | null
  activo: boolean
  cumplimiento: string | null
  semaforo: "VERDE" | "AMARILLO" | "ROJO" | "GRIS"
  mediciones: { periodo: string; valor: string; comentario: string | null }[]
  trimestres: { trimestre: string; valor: string; cumplimiento: string | null; semaforo: string }[]
}

const TONO: Record<string, Tono> = {
  VERDE: "exito",
  AMARILLO: "aviso",
  ROJO: "peligro",
  GRIS: "neutro",
}
const TEXTO: Record<string, string> = {
  VERDE: "En meta",
  AMARILLO: "Cerca",
  ROJO: "Desvío",
  GRIS: "Sin medir",
}

const num = (v: string | null) =>
  v === null ? "—" : Number(v).toLocaleString("es-VE", { maximumFractionDigits: 2 })

type Borrador = {
  nombre: string
  departamento_id: string
  unidad: string
  sentido: "MAYOR" | "MENOR"
  meta: string
  linea_base: string
  tolerancia: string
  frecuencia: "MENSUAL" | "TRIMESTRAL"
  fuente: string
}
const VACIO: Borrador = {
  nombre: "",
  departamento_id: "",
  unidad: "%",
  sentido: "MAYOR",
  meta: "",
  linea_base: "",
  tolerancia: "10",
  frecuencia: "MENSUAL",
  fuente: "",
}

/**
 * Planificación estratégica → Indicadores (28/10, minuta «Estructura de la Compañía»):
 * los indicadores de cada departamento, su última medición contra la meta y el cierre de
 * cada trimestre. Rojo es el desvío que la minuta pide alertar.
 */
export function Indicadores() {
  const usuario = usarSesion((e) => e.usuario)
  const puede = tienePermiso(usuario, "indicador:administrar")
  const clienteQuery = useQueryClient()
  const [nuevo, setNuevo] = useState(false)
  const [b, setB] = useState<Borrador>(VACIO)
  const [midiendo, setMidiendo] = useState<number | null>(null)
  const [medicion, setMedicion] = useState({ periodo: hoyLocal(), valor: "", comentario: "" })

  const lista = useQuery({
    queryKey: ["indicadores"],
    queryFn: () => api.get<Indicador[]>("/api/v1/indicadores"),
  })
  const departamentos = useQuery({
    queryKey: ["departamentos"],
    queryFn: () =>
      api.get<{ id: number; nombre: string; activo: boolean }[]>("/api/v1/departamentos"),
    enabled: nuevo,
  })
  const refrescar = () => clienteQuery.invalidateQueries({ queryKey: ["indicadores"] })
  const crear = useMutation({
    mutationFn: () =>
      api.post("/api/v1/indicadores", {
        ...b,
        departamento_id: Number(b.departamento_id),
        meta: b.meta,
        linea_base: b.linea_base === "" ? null : b.linea_base,
        tolerancia: Number(b.tolerancia),
        fuente: b.fuente || null,
      }),
    onSuccess: () => {
      refrescar()
      setNuevo(false)
      setB(VACIO)
    },
  })
  const medir = useMutation({
    mutationFn: (id: number) =>
      api.post(`/api/v1/indicadores/${id}/mediciones`, {
        periodo: medicion.periodo,
        valor: medicion.valor,
        comentario: medicion.comentario || null,
      }),
    onSuccess: () => {
      refrescar()
      setMidiendo(null)
      setMedicion({ periodo: hoyLocal(), valor: "", comentario: "" })
    },
  })

  if (lista.isPending) return <EstadoCarga etiqueta="Cargando los indicadores" />
  if (lista.isError) {
    return (
      <EstadoError
        titulo="No se pudieron cargar"
        error={lista.error}
        onReintentar={() => lista.refetch()}
      />
    )
  }
  const porDepto = new Map<string, Indicador[]>()
  for (const i of lista.data)
    porDepto.set(i.departamento, [...(porDepto.get(i.departamento) ?? []), i])
  const desvios = lista.data.filter((i) => i.semaforo === "ROJO" && i.activo).length
  const error = crear.error ?? medir.error

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          {lista.data.length === 0
            ? "Todavía no hay indicadores. Cada departamento define los suyos y los mide cada mes o cada trimestre."
            : `${lista.data.length} indicadores · ${desvios} con desvío`}
        </p>
        <div className="flex flex-wrap items-start gap-2">
          {lista.data.length > 0 && (
            <Exportar
              titulo="Indicadores de gestión"
              documento={() => documentoDeIndicadores(lista.data)}
            />
          )}
          {puede && !nuevo && (
            <Boton variante="primario" onClick={() => setNuevo(true)}>
              <Plus size={16} /> Nuevo indicador
            </Boton>
          )}
        </div>
      </div>

      {error && (
        <p role="alert" className="tarjeta p-3 text-sm" style={{ color: "var(--destructive)" }}>
          {(error as Error).message}
        </p>
      )}

      {nuevo && (
        <form
          className="tarjeta grid gap-3 p-4 sm:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault()
            crear.mutate()
          }}
        >
          <label className="text-sm sm:col-span-2">
            Qué se mide
            <input
              className="campo mt-1"
              required
              minLength={3}
              placeholder="% de despachos en menos de 5 días"
              value={b.nombre}
              onChange={(e) => setB({ ...b, nombre: e.target.value })}
            />
          </label>
          <label className="text-sm">
            Departamento
            <select
              className="campo mt-1"
              required
              value={b.departamento_id}
              onChange={(e) => setB({ ...b, departamento_id: e.target.value })}
            >
              <option value="">Elige…</option>
              {(departamentos.data ?? [])
                .filter((d) => d.activo)
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.nombre}
                  </option>
                ))}
            </select>
          </label>
          <label className="text-sm">
            Sentido
            <select
              className="campo mt-1"
              value={b.sentido}
              onChange={(e) => setB({ ...b, sentido: e.target.value as Borrador["sentido"] })}
            >
              <option value="MAYOR">Más es mejor</option>
              <option value="MENOR">Menos es mejor</option>
            </select>
          </label>
          <label className="text-sm">
            Meta
            <input
              className="campo mt-1"
              required
              type="number"
              step="any"
              value={b.meta}
              onChange={(e) => setB({ ...b, meta: e.target.value })}
            />
          </label>
          <label className="text-sm">
            Unidad
            <input
              className="campo mt-1"
              required
              value={b.unidad}
              onChange={(e) => setB({ ...b, unidad: e.target.value })}
            />
          </label>
          <label className="text-sm">
            Línea base (opcional)
            <input
              className="campo mt-1"
              type="number"
              step="any"
              value={b.linea_base}
              onChange={(e) => setB({ ...b, linea_base: e.target.value })}
            />
          </label>
          <label className="text-sm">
            Tolerancia (puntos de %)
            <input
              className="campo mt-1"
              type="number"
              min={0}
              max={100}
              value={b.tolerancia}
              onChange={(e) => setB({ ...b, tolerancia: e.target.value })}
            />
          </label>
          <label className="text-sm">
            Se mide
            <select
              className="campo mt-1"
              value={b.frecuencia}
              onChange={(e) => setB({ ...b, frecuencia: e.target.value as Borrador["frecuencia"] })}
            >
              <option value="MENSUAL">Cada mes</option>
              <option value="TRIMESTRAL">Cada trimestre</option>
            </select>
          </label>
          <label className="text-sm sm:col-span-2">
            De dónde sale el dato
            <input
              className="campo mt-1"
              placeholder="Sistema 3.0 → Libro de cuentas"
              value={b.fuente}
              onChange={(e) => setB({ ...b, fuente: e.target.value })}
            />
          </label>
          <div className="flex items-end gap-2">
            <Boton type="submit" variante="primario" disabled={crear.isPending}>
              Crear
            </Boton>
            <Boton onClick={() => setNuevo(false)}>Cancelar</Boton>
          </div>
        </form>
      )}

      {[...porDepto].map(([depto, inds]) => (
        <div key={depto} className="space-y-2">
          <h3 className="text-sm font-semibold">{depto}</h3>
          <ul className="grid gap-3 lg:grid-cols-2">
            {inds.map((i) => {
              const ultima = i.mediciones.at(-1)
              const tope = Math.max(Number(i.meta), ...i.mediciones.map((m) => Number(m.valor)), 1)
              return (
                <li key={i.id} className="tarjeta space-y-2 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-medium">{i.nombre}</span>
                    <Insignia tono={TONO[i.semaforo] ?? "neutro"}>
                      {TEXTO[i.semaforo]}
                      {i.cumplimiento !== null && ` · ${num(i.cumplimiento)} %`}
                    </Insignia>
                  </div>
                  <p className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                    Meta {num(i.meta)} {i.unidad} (
                    {i.sentido === "MAYOR" ? "más es mejor" : "menos es mejor"}) · último{" "}
                    {ultima
                      ? `${num(ultima.valor)} ${i.unidad} al ${comoFecha(ultima.periodo)}`
                      : "sin medir"}
                    {i.fuente && ` · ${i.fuente}`}
                  </p>
                  {i.mediciones.length > 0 && (
                    <div className="flex h-12 items-end gap-1" aria-hidden={true}>
                      {i.mediciones.slice(-12).map((m) => (
                        <span
                          key={m.periodo}
                          title={`${comoFecha(m.periodo)}: ${num(m.valor)}`}
                          className="flex-1 rounded-t"
                          style={{
                            height: `${(Number(m.valor) / tope) * 100}%`,
                            background: "var(--info)",
                          }}
                        />
                      ))}
                    </div>
                  )}
                  {i.trimestres.length > 0 && (
                    <p className="flex flex-wrap gap-2 text-xs">
                      {i.trimestres.map((t) => (
                        <Insignia key={t.trimestre} tono={TONO[t.semaforo] ?? "neutro"}>
                          {t.trimestre}: {num(t.valor)}
                        </Insignia>
                      ))}
                    </p>
                  )}
                  {puede && midiendo !== i.id && (
                    <Boton tamano="sm" onClick={() => setMidiendo(i.id)}>
                      Registrar medición
                    </Boton>
                  )}
                  {midiendo === i.id && (
                    <form
                      className="grid gap-2 sm:grid-cols-[9rem_7rem_1fr_auto]"
                      onSubmit={(e) => {
                        e.preventDefault()
                        medir.mutate(i.id)
                      }}
                    >
                      <input
                        aria-label="Último día del período"
                        type="date"
                        className="campo"
                        required
                        value={medicion.periodo}
                        onChange={(e) => setMedicion({ ...medicion, periodo: e.target.value })}
                      />
                      <input
                        aria-label="Valor"
                        type="number"
                        step="any"
                        className="campo"
                        required
                        placeholder={i.unidad}
                        value={medicion.valor}
                        onChange={(e) => setMedicion({ ...medicion, valor: e.target.value })}
                      />
                      <input
                        aria-label="Comentario"
                        className="campo"
                        placeholder="Comentario (opcional)"
                        value={medicion.comentario}
                        onChange={(e) => setMedicion({ ...medicion, comentario: e.target.value })}
                      />
                      <Boton
                        type="submit"
                        tamano="sm"
                        variante="primario"
                        disabled={medir.isPending}
                      >
                        Guardar
                      </Boton>
                    </form>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </section>
  )
}
